/* eslint-disable react/prop-types */
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Drawer } from "antd";
import {
    CalendarOutlined,
    CloseOutlined,
    MinusOutlined,
    PlayCircleFilled,
    PlusOutlined,
    RightOutlined,
    SearchOutlined,
    WhatsAppOutlined,
} from "@ant-design/icons";
import { useToast } from "../../../contexts/ToastContext";
import ONBOARDING_HELP_LINKS from "../../onboarding/components/onboardingLinks";
import "./helpCentreDrawer.css";

const HelpCentreDrawer = ({ open, onClose }) => {
    const { t } = useTranslation("helpCentre");
    const toast = useToast();
    const [searchTerm, setSearchTerm] = useState("");
    const [openArticleId, setOpenArticleId] = useState(null);
    const [openFaqId, setOpenFaqId] = useState(null);

    const articles = [
        {
            id: "dashboardStats",
            title: t("articleDashboardStatsTitle"),
            description: t("articleDashboardStatsDescription"),
            content: t("articleDashboardStatsContent"),
        },
        {
            id: "quickActions",
            title: t("articleQuickActionsTitle"),
            description: t("articleQuickActionsDescription"),
            content: t("articleQuickActionsContent"),
        },
        {
            id: "gettingStarted",
            title: t("articleGettingStartedTitle"),
            description: t("articleGettingStartedDescription"),
            content: t("articleGettingStartedContent"),
        },
    ];

    const videos = [
        { id: "dashboardOverview", title: t("videoDashboardOverviewTitle"), duration: "1:32" },
        { id: "fleetCalendar", title: t("videoFleetCalendarTitle"), duration: "1:32" },
    ];

    const faqs = [
        {
            id: "dashboardNumbers",
            question: t("faqDashboardNumbersQuestion"),
            answer: t("faqDashboardNumbersAnswer"),
        },
        {
            id: "weekDayToggle",
            question: t("faqWeekDayToggleQuestion"),
            answer: t("faqWeekDayToggleAnswer"),
        },
        {
            id: "calendarColors",
            question: t("faqCalendarColorsQuestion"),
            answer: t("faqCalendarColorsAnswer"),
        },
    ];

    const normalizedSearch = searchTerm.trim().toLowerCase();

    const filteredArticles = articles.filter(
        (article) =>
            !normalizedSearch ||
            article.title.toLowerCase().includes(normalizedSearch) ||
            article.description.toLowerCase().includes(normalizedSearch),
    );

    const filteredVideos = videos.filter(
        (video) => !normalizedSearch || video.title.toLowerCase().includes(normalizedSearch),
    );

    const filteredFaqs = faqs.filter(
        (faq) => !normalizedSearch || faq.question.toLowerCase().includes(normalizedSearch),
    );

    const hasResults = filteredArticles.length || filteredVideos.length || filteredFaqs.length;

    const handleClose = () => {
        setSearchTerm("");
        setOpenArticleId(null);
        setOpenFaqId(null);
        onClose();
    };

    const handleVideoClick = () => {
        toast.info({ message: t("videoComingSoon") });
    };

    return (
        <Drawer
            placement="right"
            open={open}
            onClose={handleClose}
            closable={false}
            width={520}
            destroyOnClose
            rootClassName="help-centre-drawer"
            classNames={{
                body: "!flex !h-full !flex-col !p-0",
                content:
                    "overflow-hidden rounded-none shadow-[0_20px_60px_rgba(0,0,0,0.25)] lg:rounded-l-[20px]",
            }}>
            <div className="flex items-center justify-between px-4 pt-6 sm:px-8 sm:pt-8">
                <h2 className="text-xl font-bold text-gray-900">{t("title")}</h2>
                <button
                    type="button"
                    onClick={handleClose}
                    aria-label={t("close")}
                    className="flex h-8 w-8 items-center justify-center rounded-full text-gray-500 transition-colors hover:bg-gray-100 hover:text-gray-800">
                    <CloseOutlined />
                </button>
            </div>

            <div className="px-4 py-4 sm:px-8">
                <div className="flex items-center gap-2 rounded-xl border border-gray-200 px-4 py-3 focus-within:border-[#5D5DF6]">
                    <SearchOutlined className="text-gray-400" />
                    <input
                        type="text"
                        value={searchTerm}
                        onChange={(event) => setSearchTerm(event.target.value)}
                        placeholder={t("searchPlaceholder")}
                        className="w-full border-none bg-transparent text-sm text-gray-800 outline-none placeholder:text-gray-400"
                    />
                </div>
            </div>

            <div className="border-t border-gray-200" />

            <div className="min-h-0 flex-1 overflow-y-auto px-4 py-6 sm:px-8">
                {!hasResults ? (
                    <p className="py-8 text-center text-sm text-gray-400">{t("noResults")}</p>
                ) : (
                    <>
                        {filteredArticles.length > 0 && (
                            <div>
                                <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-gray-400">
                                    {t("popularArticles")}
                                </p>
                                <div className="flex flex-col gap-2">
                                    {filteredArticles.map((article) => {
                                        const isOpen = openArticleId === article.id;
                                        return (
                                            <div
                                                key={article.id}
                                                className="rounded-2xl border border-gray-200 px-4 py-3">
                                                <button
                                                    type="button"
                                                    onClick={() => setOpenArticleId(isOpen ? null : article.id)}
                                                    className="flex w-full items-center justify-between gap-3 text-left">
                                                    <span>
                                                        <span className="block text-sm font-semibold text-gray-900">
                                                            {article.title}
                                                        </span>
                                                        <span className="block text-xs text-gray-500">
                                                            {article.description}
                                                        </span>
                                                    </span>
                                                    <RightOutlined
                                                        className={`shrink-0 text-xs text-gray-400 transition-transform ${
                                                            isOpen ? "rotate-90" : ""
                                                        }`}
                                                    />
                                                </button>
                                                {isOpen && (
                                                    <p className="mt-3 text-sm text-gray-600">{article.content}</p>
                                                )}
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>
                        )}

                        {filteredVideos.length > 0 && (
                            <div className="mt-6">
                                <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-gray-400">
                                    {t("videoHelp")}
                                </p>
                                <div className="flex flex-col gap-2">
                                    {filteredVideos.map((video) => (
                                        <button
                                            key={video.id}
                                            type="button"
                                            onClick={handleVideoClick}
                                            className="flex items-center gap-3 rounded-2xl border border-gray-200 p-3 text-left transition-colors hover:bg-gray-50">
                                            <span className="relative flex h-14 w-20 shrink-0 items-center justify-center rounded-lg bg-[#F0F0FE]">
                                                <PlayCircleFilled className="text-2xl text-[#5D5DF6]" />
                                                <span className="absolute bottom-1 left-1 rounded bg-black/70 px-1 text-[10px] leading-4 text-white">
                                                    {video.duration}
                                                </span>
                                            </span>
                                            <span className="text-sm font-semibold text-gray-900">{video.title}</span>
                                        </button>
                                    ))}
                                </div>
                            </div>
                        )}

                        {filteredFaqs.length > 0 && (
                            <div className="mt-6">
                                <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-gray-400">
                                    {t("faqs")}
                                </p>
                                <div className="flex flex-col gap-2">
                                    {filteredFaqs.map((faq) => {
                                        const isOpen = openFaqId === faq.id;
                                        return (
                                            <div key={faq.id} className="rounded-2xl border border-gray-200 px-4 py-3">
                                                <button
                                                    type="button"
                                                    onClick={() => setOpenFaqId(isOpen ? null : faq.id)}
                                                    className="flex w-full items-center justify-between gap-3 text-left">
                                                    <span className="text-sm font-medium text-gray-900">
                                                        {faq.question}
                                                    </span>
                                                    <span className="flex h-5 w-5 shrink-0 items-center justify-center text-gray-400">
                                                        {isOpen ? <MinusOutlined /> : <PlusOutlined />}
                                                    </span>
                                                </button>
                                                {isOpen && <p className="mt-3 text-sm text-gray-600">{faq.answer}</p>}
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>
                        )}
                    </>
                )}
            </div>

            <div className="border-t border-gray-100 px-4 py-6 sm:px-8">
                <p className="mb-3 text-sm font-semibold text-gray-900">{t("stillStuck")}</p>
                <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
                    <a
                        href={ONBOARDING_HELP_LINKS.whatsApp}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex flex-1 items-center justify-center gap-2 rounded-lg bg-[#00AC75] px-4 py-2.5 text-sm font-semibold text-white no-underline visited:text-white hover:bg-green-600 hover:text-white">
                        <WhatsAppOutlined />
                        {t("whatsapp")}
                    </a>
                    <a
                        href={ONBOARDING_HELP_LINKS.bookDemo}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex flex-1 items-center justify-center gap-2 rounded-lg border border-gray-200 px-4 py-2.5 text-sm font-semibold text-gray-800 no-underline visited:text-gray-800 transition-colors hover:bg-gray-50 hover:text-gray-800">
                        <CalendarOutlined />
                        {t("scheduleACall")}
                    </a>
                </div>
            </div>
        </Drawer>
    );
};

export default HelpCentreDrawer;
